/**
 * Which of the vault's two empty-looking states to show, and how far a restore
 * has got.
 *
 * Extracted from VaultView's JSX so it can be tested as itself. Inline in the
 * render it was only reachable by standing up the vault store, the gate,
 * OsScroll and the Tauri event bridge — at which point a test asserts on a
 * rendered string and still cannot say which rule produced it.
 */

/**
 * A fresh device pulls the cloud copy into `vault_incoming` and only then
 * drains it into `vault_items`. Through that whole window `items` is
 * legitimately empty — and rendering the ordinary "No items yet" there tells
 * someone whose passwords are mid-restore that their vault is empty. Same
 * screen, opposite meanings.
 *
 * @param {number} itemCount    rows already drained and visible
 * @param {number} pendingCount rows staged in vault_incoming, not yet drained
 * @returns {'restoring'|'empty'|'list'}
 */
export function vaultDisplayState(itemCount, pendingCount) {
  if (itemCount === 0 && pendingCount > 0) return 'restoring';
  if (itemCount === 0) return 'empty';
  // Partial drain: show what has landed rather than holding a spinner until the
  // last row arrives. The staged ones keep appearing underneath.
  return 'list';
}

/**
 * Progress from a count that only ever falls.
 *
 * `vault_sync_pending_count` reports what is LEFT, so the peak seen this
 * restore is the denominator — without it, done/total is 0/pending on every
 * tick and the bar sits at 0% for the entire restore.
 *
 * @param {number} peak    highest pending count seen this restore
 * @param {number} pending pending count now
 */
export function restoreProgress(peak, pending) {
  // A second device pushing mid-restore can raise pending above the peak. Grow
  // the denominator rather than emitting >100% or a negative `done`.
  const total = Math.max(peak, pending);
  const done = Math.max(0, total - pending);
  return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
}
